import { Injectable } from '@angular/core';
import { CustomerService } from './customer.service';
import { environment } from '../../environments/environment';
import { Tools } from './helpers/tools';
import { Project } from '../models/project';
import { HbmApiService } from './hbm/hbm-api.service';
import { HttpMethod } from './rest.service';
import { logError } from './helpers/sentry.helper';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  constructor(
    private customer: CustomerService,
    private hbmService: HbmApiService
  ) {}

  getAllFromUser(sapID: string): Promise<Project[]> {
    return new Promise((resolve, reject) => {
      this.getAllFromUserID(sapID).then(
        (sapLinkedProjects: Project[]) => {
          this.customer.lookForLegacyCustomerId(sapID).then(
            (legacyID: string) => {
              if (!legacyID) {
                resolve(sapLinkedProjects);
              } else {
                this.hbmService.acquireLegacyTokenWS(legacyID).then(
                  () => {
                    this.getAllFromUserID(legacyID, this.customer.legacyToken).then(
                      (legacyLinkedProjects: Project[]) => {
                        let _projects = [
                          ...sapLinkedProjects,
                          ...legacyLinkedProjects.map((lp: Project) => {
                            lp.extraDatas.isLegacy = true;
                            return lp;
                          }),
                        ].sort((a: any, b: any) => b.dateUpdate.localeCompare(a.dateUpdate));
                        resolve(_projects);
                      },
                      err => {
                        resolve(sapLinkedProjects);
                      }
                    );
                  },
                  err => {
                    // pas d'utilisateur avec cet ID coté DASSAULT
                    resolve(sapLinkedProjects);
                  }
                );
              }
            },
            err => {
              resolve(sapLinkedProjects);
            }
          );
        },
        err => {
          resolve([]);
        }
      );
    });
  }

  getAllFromUserID(userID: string, overrideToken: string = null): Promise<Project[]> {
    return new Promise((resolve, reject) => {
      let _r = [];
      this.hbmService
        .callHbmApi({
          method: HttpMethod.GET,
          url:
            environment.byMe.webServiceURL + '/api/3/projects/user/' + userID + '?limit=100&noCache=' + Math.random(),
          token: overrideToken,
        })
        .then((result: any) => {
          if (result?.rows?.length > 0) {
            let _counter: number = 0;
            for (let i = 0; i < result.rows.length; i++) {
              let _noCache: string = result.rows[i].dateUpdate.replace(/[\W]+/g, '');
              this.hbmService
                .callHbmApi({
                  method: HttpMethod.GET,
                  url:
                    environment.byMe.webServiceURL +
                    '/api/3/projects/' +
                    result.rows[i].projectID +
                    '/metadata/2' +
                    '?noCache=' +
                    _noCache,
                  token: overrideToken,
                })
                .then(
                  (metaData: any) => {
                    result.rows[i].search = Tools.removeAccents(result.rows[i].projectName.toLowerCase());
                    result.rows[i].thumb =
                      result.rows[i].bmProjURL.substring(0, result.rows[i].bmProjURL.length - 43) +
                      'images/thumbnails/thumbnail427x320.jpg?d=' +
                      _noCache;
                    result.rows[i].distributionID = this.extractDistributionID(metaData.appData.bom);
                    _r.push(Object.assign(new Project(), result.rows[i]));
                    _counter++;
                    if (_counter === result.rows.length) resolve(_r);
                  },
                  (errorResponse: any) => {
                    logError(errorResponse);
                    _counter++;
                    if (_counter === result.rows.length) resolve(_r);
                  }
                );
            }
          } else {
            resolve([]);
          }
        });
    });
  }

  extractDistributionID(stringifiedBom: string): string {
    let _regexp = /distributionID\"\:\"([\w_-]+)\"/gi;
    let _exec = _regexp.exec(stringifiedBom);
    if (_exec) return _exec[1];
    // Hack : par defaut, on retourne SdB
    return environment.byMe.bathroom.distribID;
  }

  getDetails(projectUUID: string) {
    return new Promise((resolve, reject) => {
      this.hbmService
        .callHbmApi({
          method: HttpMethod.GET,
          url: environment.byMe.webServiceURL + '/api/3/projects/' + projectUUID + '?noCache=' + Math.random(),
        })
        .then(
          (project: any) => {
            this.hbmService
              .callHbmApi({
                method: HttpMethod.GET,
                url:
                  environment.byMe.webServiceURL +
                  '/api/3/projects/' +
                  project.projectID +
                  '/metadata/2' +
                  '?noCache=' +
                  project.dateUpdate.replace(/[\W]+/g, ''),
              })
              .then(
                (metaData: any) => {
                  project.distributionID = this.extractDistributionID(metaData.appData.bom);
                  project.manualNotes = metaData.appData.manualNotes;
                  this.hbmService
                    .callHbmApi({
                      method: HttpMethod.GET,
                      url:
                        environment.byMe.webServiceURL +
                        '/api/3/projects/' +
                        project.projectID +
                        '/media?limit=50&noCache=' +
                        project.dateUpdate.replace(/[\W]+/g, ''),
                    })
                    .then(
                      (projectMedias: any) => {
                        project.medias = {
                          render360: projectMedias.rows.filter(m => m.typeMediaID === MEDIA_TYPE.RENDER_360),
                          renderHQ: projectMedias.rows.filter(m => m.typeMediaID === MEDIA_TYPE.RENDER_HQ),
                          snapshot: projectMedias.rows.filter(m => m.typeMediaID === MEDIA_TYPE.SNAPSHOT),
                          plan2D: projectMedias.rows.filter(m => m.typeMediaID === MEDIA_TYPE.PLAN_2D),
                          thumbnail: projectMedias.rows.filter(m => m.typeMediaID === MEDIA_TYPE.THUMBNAIL),
                          crosscut: projectMedias.rows.filter(m => m.typeMediaID === MEDIA_TYPE.CROSS_CUT),
                        };
                        resolve(project);
                      },
                      (errorResponse: any) => {
                        logError('getProjectDetails/media.errorResponse', errorResponse);
                        reject(errorResponse);
                      }
                    );
                },
                (errorResponse: any) => {
                  logError('getProjectDetails/metadata.errorResponse', errorResponse);
                  reject(errorResponse);
                }
              );
          },
          (errorResponse: any) => {
            logError('getProjectDetails/project.errorResponse', errorResponse);
            reject(errorResponse);
          }
        );
    });
  }

  updateManualNotes(projectUUID: string, htmlValue: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.hbmService
        .callHbmApi({
          method: HttpMethod.GET,
          url: environment.byMe.webServiceURL + '/api/3/projects/' + projectUUID + '/metadata/2',
        })
        .then(
          (metaData: any) => {
            let _currentMetaData = metaData.appData;
            _currentMetaData.manualNotes = htmlValue;

            this.hbmService
              .callHbmApi({
                method: HttpMethod.PUT,
                url: environment.byMe.webServiceURL + '/api/3/projects/' + projectUUID + '/metadata/2',
                body: { appData: _currentMetaData },
              })
              .then(
                (response: any) => {
                  // console.log(response);
                  resolve();
                },
                (errorResponse: any) => {
                  logError('updateManualNotes/putMetadata.errorResponse', errorResponse);
                  reject(errorResponse);
                }
              );
          },
          (errorResponse: any) => {
            logError('updateManualNotes/metadata.errorResponse', errorResponse);
            reject(errorResponse);
          }
        );
    });
  }

  setProjectContext(projectUUID: string, context: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.hbmService
        .callHbmApi({
          method: HttpMethod.GET,
          url: environment.byMe.webServiceURL + '/api/3/projects/' + projectUUID + '/metadata/2',
        })
        .then(
          (metaData: any) => {
            let _currentMetaData = metaData.appData;
            _currentMetaData.context = context;

            this.hbmService
              .callHbmApi({
                method: HttpMethod.PUT,
                url: environment.byMe.webServiceURL + '/api/3/projects/' + projectUUID + '/metadata/2',
                body: { appData: _currentMetaData },
              })
              .then(
                (response: any) => {
                  // console.log(response);
                  resolve();
                },
                (errorResponse: any) => {
                  logError('setProjectContext/putMetadata.errorResponse', errorResponse);
                  reject(errorResponse);
                }
              );
          },
          (errorResponse: any) => {
            logError('setProjectContext/metadata.errorResponse', errorResponse);
            reject(errorResponse);
          }
        );
    });
  }

  updateNameAndDescription(projectUUID: string, projectName: string, projectHtmlDescription: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.hbmService
        .callHbmApi({
          method: HttpMethod.PUT,
          url: environment.byMe.webServiceURL + '/api/3/projects/' + projectUUID,
          body: {
            userID: this.customer.userDatas.id,
            projectName: projectName,
            description: projectHtmlDescription,
          },
        })
        .then(
          (response: any) => {
            // console.log(response);
            resolve();
          },
          (errorResponse: any) => {
            logError('updateManualNotes/putProject.errorResponse', errorResponse);
            reject(errorResponse);
          }
        );
    });
  }

  duplicate(projectID: number, projectUUID: string, newProjectName: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.hbmService
        .callHbmApi({
          method: HttpMethod.PUT,
          url: environment.byMe.webServiceURL + '/api/3/actions/projects/' + projectID + '/duplicate',
          body: {
            userID: this.customer.userDatas.id,
            projectName: newProjectName,
            projectUUID: projectUUID,
          },
        })
        .then(
          (response: any) => {
            //console.log(response);
            resolve(response.projectUUID);
          },
          (errorResponse: any) => {
            logError('updateManualNotes/putDuplicateProject.errorResponse', errorResponse);
            reject(errorResponse);
          }
        );
    });
  }

  delete(projectID: number, isLegacy: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      let _userId: any = isLegacy ? this.customer.legacyCustomerId : this.customer.userDatas.id;
      this.hbmService
        .callHbmApi({
          method: HttpMethod.DELETE,
          url: environment.byMe.webServiceURL + '/api/3/projects/' + projectID + '/user/' + _userId,
          token: isLegacy ? this.customer.legacyToken : null,
        })
        .then(
          (response: any) => {
            // console.log(response);
            resolve();
          },
          (errorResponse: any) => {
            logError('deleteProject.errorResponse', errorResponse);
            reject(errorResponse);
          }
        );
    });
  }

  lock(projectID: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.hbmService
        .callHbmApi({
          method: HttpMethod.PUT,
          url: environment.byMe.webServiceURL + '/api/3/actions/projects/' + projectID + '/lock',
        })
        .then(
          (response: any) => {
            //console.log(response);
            resolve();
          },
          (errorResponse: any) => {
            logError('lockProject.errorResponse', errorResponse);
            reject(errorResponse);
          }
        );
    });
  }

  unlock(projectID: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.hbmService
        .callHbmApi({
          method: HttpMethod.PUT,
          url: environment.byMe.webServiceURL + '/api/3/actions/projects/' + projectID + '/unlock',
        })
        .then(
          (response: any) => {
            //console.log(response);
            resolve();
          },
          (errorResponse: any) => {
            logError('UnlockProject.errorResponse', errorResponse);
            reject(errorResponse);
          }
        );
    });
  }
}

export enum MEDIA_TYPE {
  UNKNOWN,
  RENDER_360,
  RENDER_HQ,
  SNAPSHOT,
  PLAN_2D,
  THUMBNAIL,
  CROSS_CUT,
}
